/**
 * Conflict resolution utility for import operations
 *
 * Manages per-import-session conflict resolution with "remember choice"
 * and calendar-specific duplicate suppression.
 */

import type { ImportConflictChoice } from './types'

type OpenDialogParams = {
  filename: string
  isCalendarFile: boolean
}

type OpenDialogResult = {
  choice: ImportConflictChoice
  remember: boolean
}

type OpenDialog = (
  parameters: OpenDialogParams
) => Promise<OpenDialogResult>

/**
 * Normalizes conflict choices for calendar files.
 * Calendar files cannot be duplicated, so 'duplicate' is converted to 'skip'.
 */
function normalizeChoice(
  choice: ImportConflictChoice,
  isCalendarFile: boolean
): ImportConflictChoice {
  if (isCalendarFile && choice === 'duplicate') {
    return 'skip'
  }
  return choice
}

/**
 * Creates a conflict resolver for a single import session.
 *
 * The resolver maintains state across multiple conflict callbacks,
 * allowing users to "remember" their choice for subsequent conflicts.
 *
 * Calendar files have special handling: the 'duplicate' choice is
 * automatically converted to 'skip' since calendar notes cannot be duplicated.
 *
 * @param openDialog - Function to prompt user for conflict resolution
 * @returns Resolver with resolve method for handling conflicts
 *
 * @example
 * const resolver = createConflictResolver(openDialog)
 * const choice = await resolver.resolve('note.md', false)
 */
export function createConflictResolver(openDialog: OpenDialog) {
  let rememberedChoice: ImportConflictChoice | undefined
  let remember = false

  const resolve = async (
    filename: string,
    isCalendarFile: boolean
  ): Promise<ImportConflictChoice> => {
    // If user remembered a choice, apply it (with calendar normalization)
    if (remember && rememberedChoice) {
      return normalizeChoice(rememberedChoice, isCalendarFile)
    }

    // Show dialog and get user's choice
    const { choice, remember: rememberUser } = await openDialog({
      filename,
      isCalendarFile,
    })

    // Normalize the choice for calendar files
    const normalized = normalizeChoice(choice, isCalendarFile)

    // Remember the normalized choice if user requested
    if (rememberUser) {
      rememberedChoice = normalized
      remember = true
    }

    return normalized
  }

  return { resolve }
}
